import {Injectable} from '@angular/core';
import * as _ from 'lodash-es';
import {DEFAULT_MIN_NOTIONAL, MAX_PRICE_VALUE, MAX_QUANTITY_VALUE} from '../../common/constants';
import {Security} from '../../models/security';
import {isAspreaderExchange} from '../security/aspreader-security';
import {CachedSymbolFormats} from './cached-symbol-formats';
import {
    DisplayDecimal,
    getMaxPrecisionSymbolDecimalPlaces,
    getSymbolDecimalSteps,
    SymbolDecimalsInputParams
} from './symbol-decimals';

/**
 * An object for usage as a key in WeakMap as a replacement for not defined security.
 */
const EMPTY_SECURITY_CACHE_KEY: Security = {} as Security;

/**
 * An object for usage as a key in WeakMap as a replacement for not defined security.
 * Similar to {@link EMPTY_SECURITY_CACHE_KEY}, but allows to format numbers with maximum precision.
 */
const EMPTY_SECURITY_MAX_PRECISION_CACHE_KEY: Security = {} as Security;

type DisplayDecimalFormatter = (value: number) => DisplayDecimal<string>;

@Injectable()
export class SymbolDecimalsService {

    private readonly formatBySecurityCache = new WeakMap<Security, CachedSymbolFormats>();

    public getSecurityFormats(
        security: Security,
        isMaxPrecisionIfEmptySecurity: boolean = false
    ): CachedSymbolFormats {
        const cacheKey = security || (
            isMaxPrecisionIfEmptySecurity
                ? EMPTY_SECURITY_MAX_PRECISION_CACHE_KEY
                : EMPTY_SECURITY_CACHE_KEY
        );
        let format = this.formatBySecurityCache.get(cacheKey);

        if (!format) {
            format = isMaxPrecisionIfEmptySecurity
                ? new CachedSymbolFormats(security, getMaxPrecisionSymbolDecimalPlaces())
                : new CachedSymbolFormats(security);

            this.formatBySecurityCache.set(cacheKey, format);
        }

        return format;
    }

    public getSecurityInputParams(security: Security): SymbolDecimalsInputParams {
        const securityFormats = this.getSecurityFormats(security);

        const {decimalPlaces, precision} = securityFormats;
        const steps = getSymbolDecimalSteps(security);

        const {minPrice, maxPrice, minQty, maxQty, minNotional, securityExchange} = security || {} as Security;
        const isAsecurity = isAspreaderExchange(securityExchange);

        return {
            decimals: decimalPlaces,
            steps,
            min: {
                price:    isAsecurity
                              ? -MAX_PRICE_VALUE
                              : _.defaultTo(minPrice, steps.price),
                quantity: _.defaultTo(minQty, steps.quantity),
                notional: isAsecurity
                              ? -(MAX_PRICE_VALUE * MAX_QUANTITY_VALUE)
                              : _.defaultTo(minNotional, DEFAULT_MIN_NOTIONAL)
            },
            max:      {
                price:    _.defaultTo(maxPrice, MAX_PRICE_VALUE),
                quantity: _.defaultTo(maxQty, MAX_QUANTITY_VALUE)
            },
            precision
        };
    }

}
